import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import axios from 'axios';
import { getEntityCall, editEntityCall, createEntityCall, deleteEntityCall } from '../../baseApp/entities/sagas';
import { availableEntityTypes } from '../otherEntities/utils';
import _ from './../../baseApp/shared/utils/ObjectUtils';
import DateTimeUtils from '../../baseApp/shared/utils/DateTimeUtils';
import { translateError } from '../../baseApp/shared/utils/Errors';
import { getUserPageRefresh } from '../../baseApp/auth/sagas';

const controllerUrl = '/meetingRoom/';

const meetingRoomToCalendarStatus = [
    "PENDING",
    "PENDING_UPDATE",
    "ACCEPTED",
    "END_EARLY",
    "PENDING_AUTO_CHECKOUT",
    "AUTO_CHECKOUT"
];

const getMeetingRoomProviderListCall = searchFields => new Promise((resolve, reject) => {
        axios.get('/meetingRooms/providers/list', {
            params: {
                ...searchFields
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const searchMeetingRoomsCall = searchFields => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'search', {
            params: {
                ...searchFields
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const getEventsForCalendarCall = (meetingRoomId, startDate, endDate) => new Promise((resolve, reject) => {
    axios.get('/meetingRooms/schedule/list', {
        params: {
            meetingRoomEntityId: meetingRoomId,
            plannedStart: startDate,
            plannedEnd: endDate,
            status: meetingRoomToCalendarStatus
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const searchMeetingRoomsScheduleEvents = searchFields =>
    new Promise((resolve, reject) => {
        axios.get('/meetingRooms/schedule/events/search', {
            params: {
                ...searchFields
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
    });

function* searchMeetingRoomsRequest({ searchFields }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        if (searchFields.capacity) {
            const { min, max } = getMinAndMaxCapacityFromValues(searchFields.capacity);
            searchFields.minCapacity = min;
            searchFields.maxCapacity = max;
            delete searchFields.capacity;
        }

        const searchResults = yield call(searchMeetingRoomsCall, searchFields);
        const parentIds = searchResults.content.map(meetingRoom => meetingRoom.parentId);
        const uniqueParentIds = [...new Set(parentIds)];

        const parentEntities = yield all(
            uniqueParentIds
                .filter(id => !_.isNil(id))
                .map(id => call(getEntityCall, id))
        );
        const searchResultsWithParents = {
            ...searchResults,
            content: searchResults.content.map(
                meetingRoom => ({
                    ...meetingRoom,
                    parent: parentEntities.find(mr => mr.id === meetingRoom.parentId)
                })
            )
        };
        yield put({
            type: actionTypes.MEETING_ROOM_SEARCH_SUCCESS,
            payload: searchResultsWithParents
        });
    } catch (e) {
        yield put({
            type: actionTypes.MEETING_ROOM_SEARCH_FAILED,
            payload: e
        });
    }
}

function* getMeetingRoomProviderListRequest({ filter }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }

        const meetingRoomProvider = yield call(getMeetingRoomProviderListCall, filter);

        yield put({
            type: actionTypes.GET_MEETING_ROOM_PROVIDER_LIST_SUCCESS,
            payload: meetingRoomProvider.map(provider => ({ value: provider.id, label: provider.displayName }))
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_MEETING_ROOM_PROVIDER_LIST_FAILED
        });
    }
}

function* getMeetingRoomRequest({ meetingRoomId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const meetingRoom = yield call(getEntityCall, meetingRoomId);

        meetingRoom.parent = meetingRoom.parentId;

        yield put({
            type: actionTypes.GET_MEETING_ROOM_SUCCESS,
            payload: meetingRoom
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_MEETING_ROOM_FAILED
        });
    }
}

function* createMeetingRoomRequest({ meetingRoomData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        meetingRoomData.parentId = meetingRoomData.parent;
        meetingRoomData.type = availableEntityTypes.meetingRoom;
        meetingRoomData.providerId = meetingRoomData.provider;

        const meetingRoom = yield call(createEntityCall, meetingRoomData);

        yield put({
            type: actionTypes.CREATE_MEETING_ROOM_SUCCESS,
            payload: meetingRoom
        });
    } catch ({ response }) {
        yield put({
            type: actionTypes.CREATE_MEETING_ROOM_FAILED,
            payload: translateError('meetingRooms', response.status, response.data.status)
        });
    }
}

function* editMeetingRoomRequest({ meetingRoomId, meetingRoomData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        meetingRoomData.parentId = meetingRoomData.parent;
        meetingRoomData.type = availableEntityTypes.meetingRoom;
        meetingRoomData.providerId = meetingRoomData.provider;

        const meetingRoom = yield call(editEntityCall, meetingRoomId, meetingRoomData);

        yield put({
            type: actionTypes.EDIT_MEETING_ROOM_SUCCESS,
            payload: meetingRoom
        });
    } catch ({ response }) {
        yield put({
            type: actionTypes.EDIT_MEETING_ROOM_FAILED,
            payload: translateError('meetingRooms', response.status, response.data.status)
        });
    }
}

function* deleteMeetingRoomRequest({ meetingRoomId }) {
    try {
        yield call(deleteEntityCall, meetingRoomId);

        yield put({
            type: actionTypes.DELETE_MEETING_ROOM_SUCCESS
        });
    } catch (e) {
        yield put({
            type: actionTypes.DELETE_MEETING_ROOM_FAILED
        });
    }
}

function* getMeetingRoomEventsAsCalendarEvents({ meetingRoomId, start, end }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const meetingRoomEvents = yield call(getEventsForCalendarCall, meetingRoomId, start.valueOf(), end.valueOf());

        const eventsForCalendar = meetingRoomEvents.map(event => ({
            title: event.ownerName,
            start: event.realStart ? new Date(event.realStart) : new Date(event.plannedStart),
            end: event.realEnd ? new Date(event.realEnd) : new Date(event.plannedEnd),
            allDay: false,
            resource: null,
        }));

        yield put({
            type: actionTypes.GET_SCHEDULED_EVENTS_FOR_CALENDAR_SUCCESS,
            payload: eventsForCalendar
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_SCHEDULED_EVENTS_FOR_CALENDAR_FAILED,
            payload: e
        });
    }
}


function* searchMeetingRoomsScheduleEventsEntriesRequest({ searchFields }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        let searchResults = yield call(searchMeetingRoomsScheduleEvents, searchFields);

        searchResults.content = searchResults.content.map(entry => ({
            ...entry,
            creationTimestamp: DateTimeUtils.getDateTimeFromUnix(entry.creationTimestamp),
            entryStartEnd: DateTimeUtils.getDateFromUnix(entry.scheduleEntryStartTimestamp) + ' ' + DateTimeUtils.getTimeWithoutSecondsFromUnix(entry.scheduleEntryStartTimestamp) + 
                ' - ' + DateTimeUtils.getDateFromUnix(entry.scheduleEntryEndTimestamp) + ' ' + DateTimeUtils.getTimeWithoutSecondsFromUnix(entry.scheduleEntryEndTimestamp)
        }));

        yield put({
            type: actionTypes.SCHEDULE_EVENT_ENTRIES_SEARCH_SUCCESS,
            payload: searchResults
        });
    } catch ({ response }) {
        yield put({
            type: actionTypes.SCHEDULE_EVENT_ENTRIES_SEARCH_FAILED,
            payload: response
        });
    }
}

const meetingRoomsSagas = [
    takeEvery(actionTypes.GET_MEETING_ROOM_PROVIDER_LIST_REQUEST, getMeetingRoomProviderListRequest),
    takeEvery(actionTypes.MEETING_ROOM_SEARCH_REQUEST, searchMeetingRoomsRequest),
    takeEvery(actionTypes.GET_MEETING_ROOM_REQUEST, getMeetingRoomRequest),
    takeEvery(actionTypes.CREATE_MEETING_ROOM_REQUEST, createMeetingRoomRequest),
    takeEvery(actionTypes.EDIT_MEETING_ROOM_REQUEST, editMeetingRoomRequest),
    takeEvery(actionTypes.DELETE_MEETING_ROOM_REQUEST, deleteMeetingRoomRequest),
    takeEvery(actionTypes.GET_SCHEDULED_EVENTS_FOR_CALENDAR_REQUEST, getMeetingRoomEventsAsCalendarEvents),
    takeEvery(actionTypes.SCHEDULE_EVENT_ENTRIES_SEARCH_REQUEST, searchMeetingRoomsScheduleEventsEntriesRequest)
];

export {
    meetingRoomsSagas
};

function getMinAndMaxCapacityFromValues(capacityValues = "") {
    //capacityValues ='{ minCapacity: 5,  maxCapacity: 10 }'

    let min = undefined,
        max = undefined;

    const capacityObj = JSON.parse(capacityValues);
    if (capacityObj.minCapacity) {
        min = capacityObj.minCapacity
    }
    if (capacityObj.maxCapacity) {
        max = capacityObj.maxCapacity
    }

    return {
        min,
        max
    };
}